'use client'

import { useEffect, useState } from 'react'
import classnames from 'classnames'
import styles from './RestaurantOpenClosedBlurbNewFormat.module.scss'
import { getIfRestaurantIsOpenNewFormat, getMergedTimeRangeHoursWithTimeRangeHolidayHours } from '@/utils/restaurant'
import { TIME_ZONES } from '@/data'
import useI18n from '@/hooks/use-i18n'

type RestaurantOpenClosedBlurbNewFormatProps = {
  timeZone?: string
  weeklyOpeningHours?: WeeklyOpeningHours
  holidayHours: SanityLocationHolidayHours[]
  className?: string
  isComingSoon?: boolean
  isSoftOpening?: boolean
  isTemporarilyClosed?: boolean
}

// Helper function to get the correct closing hour, considering overnight hours
/**
 * KEEPING THIS FUNCTION HERE TO PREVENT HYDRATION ERRORS
 */
const getCurrentClosingHour = (
  weeklyOpeningHours: WeeklyOpeningHours,
  currentTime: Date,
  timeZone: string,
): string | null => {
  if (!weeklyOpeningHours || !timeZone) return null

  const restaurantTZ = TIME_ZONES[timeZone as keyof typeof TIME_ZONES]?.zone
  if (!restaurantTZ) return null

  // Get current time in restaurant timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: restaurantTZ,
    hour12: false,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
  const parts = formatter.formatToParts(currentTime)
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00'

  const currentWeekday = get('weekday').toLowerCase()
  const currentHour = parseInt(get('hour'))
  const currentMinute = parseInt(get('minute'))
  const currentTimeInMinutes = currentHour * 60 + currentMinute

  // Get today's hours
  const todayHours = weeklyOpeningHours[currentWeekday as keyof WeeklyOpeningHours] || []

  // Get yesterday's hours
  const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const currentDayIndex = dayIndex.indexOf(currentWeekday)
  const prevDayIndex = (currentDayIndex + 6) % 7
  const prevWeekday = dayIndex[prevDayIndex]
  const yesterdayHours = weeklyOpeningHours[prevWeekday as keyof WeeklyOpeningHours] || []

  // Check if we're in overnight hours from yesterday
  for (const range of yesterdayHours) {
    if (!range.open || !range.close || range.isClosed) continue

    const [openH, openM] = range.open.split(':').map(Number)
    const [closeH, closeM] = range.close.split(':').map(Number)

    const openMinutes = openH * 60 + openM
    let closeMinutes = closeH * 60 + closeM

    // Handle overnight hours (close time is 00:00 or earlier than open time)
    const isOvernightOperation = closeMinutes === 0 || closeMinutes < openMinutes

    if (isOvernightOperation) {
      // If close time is 00:00, treat as end of day (24:00)
      if (closeMinutes === 0) {
        closeMinutes = 24 * 60
      }

      // Check if current time is in the "next day" part of overnight hours
      if (currentTimeInMinutes < closeMinutes) {
        // For overnight hours, we want the close time from yesterday's hours
        const [closeHour, closeMin] = range.close.split(':').map(Number)
        if (closeHour === 0) {
          // 00:00 becomes 24:00 (midnight)
          return '12:00 AM'
        } else {
          // Format the close time
          const amPmHour = closeHour > 12 ? closeHour - 12 : closeHour === 0 ? 12 : closeHour
          const amPm = closeHour >= 12 ? 'PM' : 'AM'
          const minutes = closeMin < 10 ? `0${closeMin}` : closeMin
          return `${amPmHour}:${minutes} ${amPm}`
        }
      }
    }
  }

  // Get today's hours to find closing time
  for (const range of todayHours) {
    if (!range.close || range.isClosed) continue

    const [closeHour, closeMin] = range.close.split(':').map(Number)
    const amPmHour = closeHour > 12 ? closeHour - 12 : closeHour === 0 ? 12 : closeHour
    const amPm = closeHour >= 12 ? 'PM' : 'AM'
    const minutes = closeMin < 10 ? `0${closeMin}` : closeMin
    return `${amPmHour}:${minutes} ${amPm}`
  }

  return null
}

const RestaurantOpenClosedBlurbNewFormat = ({
  className,
  timeZone,
  weeklyOpeningHours,
  holidayHours,
  isComingSoon,
  isSoftOpening,
  isTemporarilyClosed,
}: RestaurantOpenClosedBlurbNewFormatProps) => {
  const { i18n } = useI18n()
  const [isClient, setIsClient] = useState(false)
  const [currentTime, setCurrentTime] = useState<Date | null>(null)

  /**
   * PREVENT HYDRATION MISMATCH - KEEP
   */
  useEffect(() => {
    setIsClient(true)
    setCurrentTime(new Date())

    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  if (isTemporarilyClosed) {
    return (
      <div
        className={classnames(styles.RestaurantOpenClosedBlurb, className)}
        data-is-open={'false'}
        data-is-coming-soon={isComingSoon === true}
      >
        <span className={styles.dot} />
        <span className={styles.text}>Temporarily Closed</span>
      </div>
    )
  }
  // Return null if essential data is missing
  if (!timeZone || !weeklyOpeningHours) return null

  if (!isClient || !currentTime) {
    return null
  }

  // Merge weekly hours with holiday hours using new function
  const adjustedHours = getMergedTimeRangeHoursWithTimeRangeHolidayHours(weeklyOpeningHours, holidayHours)

  // Check if restaurant is currently open
  const isOpen = getIfRestaurantIsOpenNewFormat(timeZone, adjustedHours)

  // Get today's closing hour
  const closingHour = getCurrentClosingHour(adjustedHours, currentTime, timeZone)

  // Determine display text based on status
  let text = i18n('locationOpenUntil', { time: closingHour || '' })
  if (!isOpen) {
    text = i18n('locationClosed')
  }
  if (isComingSoon === true || isSoftOpening === true) {
    text = i18n('steamingSoon')
  }

  return (
    <div
      className={classnames(styles.RestaurantOpenClosedBlurb, className)}
      data-is-open={isOpen ? 'true' : 'false'}
      data-is-coming-soon={isComingSoon === true || isSoftOpening === true}
    >
      <span className={styles.dot} />
      <span className={styles.text}>{text}</span>
    </div>
  )
}

RestaurantOpenClosedBlurbNewFormat.displayName = 'RestaurantOpenClosedBlurbNewFormat'

export default RestaurantOpenClosedBlurbNewFormat
